//src/hooks/useJobPositions.ts
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createJobPosition,
  getJobPosition,
  listJobPositions,
  setJobPositionActive,
  updateJobPosition,
  type JobPositionUpsertIn,
} from "@/services/jobPositions";
import { useSelectedClientCompany } from "@/hooks/useSelectedClientCompany";

export function useJobPositions(includeInactive = false) {
  const { selectedClientCompanyId } = useSelectedClientCompany();

  return useQuery({
    queryKey: ["job_positions", selectedClientCompanyId, { includeInactive }],
    queryFn: () => listJobPositions(includeInactive),
    enabled: !!selectedClientCompanyId,
    staleTime: 30_000,
  });
}

export function useJobPositionDetail(id?: string) {
  const { selectedClientCompanyId } = useSelectedClientCompany();

  return useQuery({
    queryKey: ["job_position", selectedClientCompanyId, id],
    queryFn: () => getJobPosition(id!),
    enabled: !!id && !!selectedClientCompanyId,
  });
}

export function useCreateJobPosition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: JobPositionUpsertIn) => createJobPosition(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["job_positions"] });
    },
  });
}

export function useUpdateJobPosition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: JobPositionUpsertIn }) =>
      updateJobPosition(id, body),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["job_positions"] });
      queryClient.invalidateQueries({ queryKey: ["job_position", variables.id] });
    },
  });
}

export function useSetJobPositionActive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      setJobPositionActive(id, is_active),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["job_positions"] });
      queryClient.invalidateQueries({ queryKey: ["job_position", variables.id] });
    },
  });
}
